import FileCopyIcon from "@mui/icons-material/FileCopyOutlined";
import DownloadIcon from "@mui/icons-material/FileDownloadOutlined";
import PlayArrowOutlined from "@mui/icons-material/PlayArrowOutlined";
import StarFilledIcon from "@mui/icons-material/StarOutlined";
import StarOutlineIcon from "@mui/icons-material/StarOutlineOutlined";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardActionArea from "@mui/material/CardActionArea";
import CardMedia from "@mui/material/CardMedia";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { Link } from "react-router-dom";

import APIconButton from "../../components/buttons/APIconButton";
import APInvalidCalculationToggle from "../../components/buttons/APInvalidCalculationToggle";
import AIButton from "../../components/buttons/APOutlineButtons/AIButton";
import CatchEButton from "../../components/buttons/CatchEButton";
import LockedButton from "../../components/buttons/LockedButon";
import VisibilityButton from "../../components/buttons/VisibilityButton";
import APCheckbox from "../../components/form/APCheckbox";
import RestrictedAppScope from "../../components/RestrictedAppScope";
import { formatDate } from "../../helpers/dates";
import { getPurchaseTypeLabel } from "../../helpers/quotes";
import { useAuth } from "../../providers/AuthProvider";
import {
  PeroidLabelMap,
  QuoteCalculationStatus,
  QuotePayPeriod,
  VehiclePurchaseType,
} from "../../types/Quote";

type QuoteCardButtonActions = {
  onSetPrimary?: (quoteId: string) => Promise<void>;
  onDownload?: (quoteId: string) => Promise<void>;
  onToggleCompare?: (quoteId: string) => Promise<void>;
  onClone?: (quoteId: string) => Promise<void>;
  onEdit?: (quoteId: string) => Promise<void>;
  onAIReview?: (quoteId: string) => Promise<void>;
  onSetVisibility?: (quoteId: string, isVisible: boolean) => Promise<void>;
  onSetLocked?: (quoteId: string, isLocked: boolean) => Promise<void>;
  onNext?: () => Promise<void>;
  onDelete?: (quoteId: string) => Promise<void>;
};

export type QuoteCardActions = {
  actionHandlers: QuoteCardButtonActions;
  bottomActions?: (keyof QuoteCardButtonActions)[];
  topActions?: (keyof QuoteCardButtonActions)[];
  onCardClick?: (quoteId: string) => Promise<void>;
};

export type QuoteCardConfig = {
  isLocked: boolean;
  isPrimary: boolean;
  isVisible: boolean;
  isBeingCompared: boolean;
  disablePrimaryToggle: boolean;
  hidePrimaryToggle: boolean;
  disableCardLink: boolean;
  hideClone: boolean;
  hidePricing: boolean;
  hideAIReview: boolean;
  shouldPromptForAIConsent: boolean;
  showInvalidCalculationToggle: boolean;
  compareTextOverride?: string;
  highlightCompare?: boolean;
};

interface QuoteCardProps {
  id: string;
  dossierId: string;
  name: string;
  vehicleMake: string;
  vehicleModel: string;
  vehicleYear: string;
  vehicleImage: string | null;
  vehicleOnroadCosts: string;
  purchaseType: VehiclePurchaseType;
  annualKm: string;
  term: number;
  startDate: string | null;
  endDate: string | null;
  savings: number;
  netCosts: string;
  payPeriod: QuotePayPeriod;
  catchEQuoteUrl: string;
  actions: QuoteCardActions;
  config: QuoteCardConfig;
  calculationStatus: QuoteCalculationStatus | null;
}

const QuoteCard = ({
  id,
  dossierId,
  name,
  vehicleMake,
  vehicleModel,
  vehicleYear,
  vehicleImage,
  vehicleOnroadCosts,
  purchaseType,
  annualKm,
  term,
  startDate,
  endDate,
  savings,
  netCosts,
  payPeriod,
  catchEQuoteUrl,
  actions,
  config,
  calculationStatus,
}: QuoteCardProps) => {
  const imageUrl = vehicleImage ?? "/photo-placeholder.png";
  const { getScopedPageUrl } = useAuth();

  const renderPrimaryToggle = () => {
    return (
      <>
        {config.isPrimary ? (
          <StarFilledIcon sx={{ m: 1 }} color="secondary" />
        ) : (
          <IconButton
            size="medium"
            aria-label="favourite-quote"
            color="onSurface"
            onClick={() => actions.actionHandlers.onSetPrimary?.(id)}
            disabled={config.disablePrimaryToggle}
            sx={{
              visibility: config.hidePrimaryToggle ? "hidden" : undefined,
            }}
          >
            <StarOutlineIcon />
          </IconButton>
        )}
      </>
    );
  };

  const renderToggleCompare = () => {
    return (
      <APCheckbox
        name="compare"
        label={config.compareTextOverride ?? "Compare"}
        defaultValue={false}
        value={config.isBeingCompared}
        onChange={() => actions.actionHandlers.onToggleCompare?.(id)}
      />
    );
  };

  const renderDownloadButton = () => {
    return (
      <IconButton
        size="medium"
        aria-label="download-quote"
        color="onSurface"
        onClick={() => actions.actionHandlers.onDownload?.(id)}
      >
        <DownloadIcon />
      </IconButton>
    );
  };

  const renderCloneButton = () => {
    return (
      <IconButton
        size="medium"
        aria-label="clone-quote"
        color="onSurface"
        onClick={() => actions.actionHandlers.onClone?.(id)}
      >
        <FileCopyIcon />
      </IconButton>
    );
  };

  const renderSetLockedButton = () => {
    return (
      <RestrictedAppScope scope="internal">
        <LockedButton
          isLocked={config.isLocked}
          onToggle={(isLocked: boolean) =>
            actions.actionHandlers.onSetLocked?.(id, isLocked)
          }
        />
      </RestrictedAppScope>
    );
  };

  const renderSetVisibilityButton = () => {
    return (
      <RestrictedAppScope scope="internal">
        <VisibilityButton
          isVisible={config.isVisible}
          onToggle={(isVisible: boolean) =>
            actions.actionHandlers.onSetVisibility?.(id, isVisible)
          }
        />
      </RestrictedAppScope>
    );
  };

  const renderDeleteButton = () => {
    return (
      <APIconButton
        iconName="delete"
        onClick={() => actions.actionHandlers.onDelete?.(id)}
      />
    );
  };

  const renderCatchEButton = () => {
    return (
      <RestrictedAppScope scope="internal">
        <CatchEButton url={catchEQuoteUrl} />
      </RestrictedAppScope>
    );
  };

  return (
    <Card
      sx={{
        display: "flex",
        backgroundColor: "surface.main",
        flexDirection: "column",
        borderColor:
          config.highlightCompare && config.isBeingCompared
            ? "primary.main"
            : undefined,
        py: 1,
      }}
      variant="outlined"
    >
      {config.showInvalidCalculationToggle && (
        <Box sx={{ pl: 1, width: "100%" }}>
          {calculationStatus === "invalid" && (
            <APInvalidCalculationToggle
              quoteId={id}
              dossierId={dossierId}
              status="invalid"
            />
          )}
          {calculationStatus === "valid-override" && (
            <APInvalidCalculationToggle
              quoteId={id}
              dossierId={dossierId}
              status="valid-override"
            />
          )}
        </Box>
      )}
      {actions.topActions && (
        <Box
          sx={{
            display: "flex",
            flexDirecion: "row",
            justifyContent: "space-between",
          }}
          mx={{
            xs: 1,
            sm: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center" }}>
            {actions.topActions?.includes("onToggleCompare") &&
              renderToggleCompare()}
          </Box>

          <Box sx={{ display: "flex", gap: 1 }}>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              {actions.topActions?.includes("onSetLocked") &&
                renderSetLockedButton()}
            </Box>

            <Box sx={{ display: "flex", alignItems: "center" }}>
              {actions.topActions?.includes("onSetVisibility") &&
                renderSetVisibilityButton()}
            </Box>

            <Box sx={{ display: "flex", alignItems: "center" }}>
              {actions.topActions?.includes("onClone") && renderCloneButton()}
            </Box>

            <Box sx={{ display: "flex", alignItems: "center" }}>
              {actions.topActions?.includes("onDownload") &&
                !config.hidePricing &&
                renderDownloadButton()}
            </Box>

            <Box sx={{ display: "flex", alignItems: "center" }}>
              {actions.topActions?.includes("onSetPrimary") &&
                renderPrimaryToggle()}
            </Box>
          </Box>
        </Box>
      )}
      <CardActionArea
        sx={{
          px: {
            xs: 1,
            sm: 2,
          },
          py: 1,
          display: "flex",
          flexDirection: "column",
          flex: 1,
          justifyContent: "space-between",
        }}
        component={config.disableCardLink ? Box : Link}
        to={
          config.disableCardLink ? undefined : getScopedPageUrl("viewQuote", id)
        }
        onClick={() => {
          actions.onCardClick?.(id);
        }}
        disableRipple={config.disableCardLink && !actions.onCardClick}
      >
        <Box sx={{ display: "flex", alignSelf: "start", maxWidth: "100%" }}>
          <CardMedia
            component="img"
            sx={{
              minWidth: "5rem",
              flex: 2,
              mr: 1,
              ml: {
                xs: -1,
                sm: -2,
              },
              maxWidth: "20rem",
            }}
            image={imageUrl}
            alt={`${vehicleYear} ${vehicleMake} ${vehicleModel}`}
          />
          <Box
            sx={{
              display: "flex",
              flex: 3,
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            <Box>
              <Typography
                variant="titleMedium"
                color="onSurface.main"
                sx={{ minHeight: "1.5rem" }}
              >
                {name}
              </Typography>
              <Typography variant="bodyMedium" color="onSurface.main">
                {vehicleYear} {vehicleMake} {vehicleModel}
              </Typography>
            </Box>
            <Box sx={{ mt: 1 }}>
              <Typography variant="bodyLarge" color="onSurface.main" noWrap>
                {vehicleOnroadCosts} - {getPurchaseTypeLabel(purchaseType)}
              </Typography>
              <Stack spacing={1} direction="row">
                <Typography variant="bodyLarge" color="onSurface.main">
                  {annualKm}km
                </Typography>
                <Typography variant="bodyLarge" color="onSurface.main">
                  {term}mths
                </Typography>
              </Stack>
              {startDate && endDate && (
                <Typography variant="bodyMedium" color="onSurfaceVariant.main">
                  {formatDate(startDate)} - {formatDate(endDate)}
                </Typography>
              )}
            </Box>
          </Box>
        </Box>
        {!config.hidePricing && (
          <Box
            sx={{
              display: "flex",
              mt: 2,
              alignSelf: "stretch",
              justifyContent: "space-between",
              columnGap: 2,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Box sx={{ mr: 1, p: 0.5, backgroundColor: "secondary.main" }}>
                <Typography
                  variant="headlineSmall"
                  color="onPrimary.main"
                  sx={{ whiteSpace: "nowrap" }}
                >
                  {savings || savings === 0 ? savings.toFixed(0) : ""} %
                </Typography>
              </Box>
              <Typography variant="titleLarge" color="primary.main">
                Savings
              </Typography>
            </Box>
            <Box
              sx={{ display: "flex", alignItems: "center", textAlign: "right" }}
            >
              <Typography
                variant="titleSmall"
                color="onSurfaceVariant.main"
                sx={{ textTransform: "capitalize" }}
              >
                {PeroidLabelMap[payPeriod]}ly Net Cost^
              </Typography>
              <Box sx={{ ml: 0.5, p: 1, backgroundColor: "primary.main" }}>
                <Typography variant="titleMedium" color="onPrimary.main">
                  {netCosts}
                </Typography>
              </Box>
            </Box>
          </Box>
        )}
      </CardActionArea>
      {actions.bottomActions && (
        <Box
          sx={{
            display: "flex",
            flexDirecion: "row",
            justifyContent: "space-between",
            px: {
              xs: 1,
              sm: 2,
            },
            mt: {
              xs: 1,
              sm: 2,
            },
          }}
        >
          <Box sx={{ display: "flex" }}>
            {!config.hideAIReview &&
              actions.bottomActions?.includes("onAIReview") && (
                <Box>
                  <AIButton
                    type="vehicle-review"
                    onClick={() => actions.actionHandlers.onAIReview?.(id)}
                    shouldPromptForAIConsent={config.shouldPromptForAIConsent}
                    textOverride="AI Review"
                  />
                </Box>
              )}

            {actions.bottomActions?.includes("onSetPrimary") &&
              renderPrimaryToggle()}

            {actions.bottomActions?.includes("onDownload") &&
              !config.hidePricing &&
              renderDownloadButton()}

            {!config.hideClone &&
              actions.bottomActions?.includes("onClone") &&
              renderCloneButton()}

            {actions.bottomActions?.includes("onDelete") &&
              !config.isPrimary &&
              !config.isLocked &&
              renderDeleteButton()}

            {!config.isPrimary &&
              actions.bottomActions?.includes("onSetVisibility") &&
              renderSetVisibilityButton()}

            {actions.bottomActions?.includes("onSetLocked") &&
              renderSetLockedButton()}

            <Box sx={{ display: "flex", alignItems: "center" }}>
              {renderCatchEButton()}
            </Box>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {!config.isLocked && actions.bottomActions?.includes("onEdit") && (
              <Button
                size="large"
                color="primary"
                variant="outlined"
                onClick={() => actions.actionHandlers.onEdit?.(id)}
              >
                Edit
              </Button>
            )}
            {config.isPrimary && (
              <RestrictedAppScope scope="external">
                <Button
                  size="large"
                  color="secondary"
                  variant="contained"
                  disableElevation={true}
                  startIcon={<PlayArrowOutlined />}
                  onClick={() => actions.actionHandlers.onNext?.()}
                >
                  Next
                </Button>
              </RestrictedAppScope>
            )}
          </Box>
        </Box>
      )}
    </Card>
  );
};

export default QuoteCard;
